import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useUser } from "../../contexts/UserContext";
import { useLessons } from "../../contexts/LessonsContext";
import SartLesson from "./SartLesson";
import DefaultCourseImg from "../../Assests/Images/default-course.jpg";
import DefaultTeacherImg from "../../Assests/Images/img_avatar1.png";
import KebabMenu from "../common/KebabMenu";
import SatisfactionRatingSystem from "../ui/SatisfactionRatingSystem";

const IconEdit = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M14.06 4.94l3.75 3.75" fill="none" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconReport = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <path d="M6 4h12v16H6z" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.5" y1="9" x2="15.5" y2="9" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.5" y1="12.5" x2="15.5" y2="12.5" stroke="currentColor" strokeWidth="1.6" />
    <line x1="8.5" y1="16" x2="13" y2="16" stroke="currentColor" strokeWidth="1.6" />
  </svg>
);

const IconCancel = () => (
  <svg width="16" height="16" viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.6" />
    <path d="M9 9l6 6M15 9l-6 6" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const buildTeacherFromCourse = (c) => {
  if (!c || typeof c !== "object") return null;
  const t = (c.teacher && typeof c.teacher === "object" ? c.teacher : null) ||
    (c.Teacher && typeof c.Teacher === "object" ? c.Teacher : null) || null;
  const first = (t?.firstName || t?.first_name || "") || (c.teacherFirstName || c.teacher_first_name || "");
  const last  = (t?.lastName  || t?.last_name  || "") || (c.teacherLastName  || c.teacher_last_name  || "");
  const name  = t?.name || c.teacherName || c.teacher_name || `${first} ${last}`.trim();
  const pictureUrl = t?.pictureUrl || t?.picture_url || t?.image || t?.avatar ||
    c.teacherPictureUrl || c.teacher_picture_url || c.teacherImage || c.teacher_image || null;
  const id = t?.id ?? t?.teacherId ?? c.teacherId ?? c.teacher_id ?? null;
  if (!(name || pictureUrl || id || first || last)) return null;
  return { id, firstName: first || "", lastName: last || "", name: name || "מורה", pictureUrl };
};

const toLocalDateTime = (yyyyMmDd, hhmm = "00:00") => {
  if (!yyyyMmDd) return null;
  try {
    const [Y, M, D] = String(yyyyMmDd).split("-").map(Number);
    const [h, m]    = String(hhmm || "00:00").split(":").map(Number);
    const dt = new Date(Y, (M || 1) - 1, D || 1, h || 0, m || 0, 0);
    return Number.isNaN(dt.getTime()) ? null : dt;
  } catch { return null; }
};

export default function LessonCard({
  lesson, course, teacherFallback, role,
  canJoin, isInCart, onAddToCart, onShowRecording,
  onRequestEdit, canEdit, onShowParticipants, onCancelSession, onRateLesson,
}) {
  const { role: userRole, userType, user, showAlert } = useUser();
  const [startingLessonId, setStartingLessonId] = useState(null);

  const isTeacherCtx  = userRole === "teacher" || userType === "teacher";
  const effectiveRole = role ?? (isTeacherCtx ? "teacher" : "student");
  const isTeacher     = effectiveRole === "teacher";

  // Independent of the `role` prop, which OnlineClass.jsx deliberately
  // passes as 'student' for a supplier too (canJoin/LessonCard weren't
  // built to know about a third role). This component needs its own
  // read of who's actually logged in for two things the 'student'
  // label alone can't distinguish: never requiring payment, and never
  // allowing rating.
  const isSupplier = userRole === "supplier" || userType === "supplier";

  const {
    canJoin: canJoinCtx,
    isInCart: isInCartCtx,
    addToCart: addToCartCtx,
    rateLesson: rateLessonCtx,
    newZoomMeeting,
    getAvailableMeetings,
    markStudentJoinedLesson,
    updateLessonAttendance,
    getServerTime,
  } = useLessons();

  const teacher = useMemo(() => {
    const t1 = lesson?.teacher && typeof lesson.teacher === "object" ? lesson.teacher : null;
    const t2 = teacherFallback && typeof teacherFallback === "object" ? teacherFallback : null;
    const t3 = buildTeacherFromCourse(course);
    const t  = t1 || t2 || t3 || {};
    const first = t.firstName || t.first_name || "";
    const last  = t.lastName  || t.last_name  || "";
    return { ...t, name: t.name || `${first} ${last}`.trim() || "מורה" };
  }, [lesson?.teacher, teacherFallback, course]);

  const instructor    = teacher.name || "מורה";
  const subject       = lesson.courseSubject || course?.subject || lesson?.course?.subject || "";
  const courseName    = lesson.courseTitle   || course?.title   || lesson?.course?.title   || "";
  const courseImageUrl = lesson.courseImageUrl || course?.image || course?.courseImageUrl ||
    course?.imageUrl || lesson?.course?.image || null;

  const courseImage = useMemo(
    () => (courseImageUrl?.toString().trim() ? courseImageUrl : DefaultCourseImg),
    [courseImageUrl]
  );

  const teacherImage = teacher.pictureUrl || teacher.picture_url || teacher.image ||
    teacher.avatar || teacher.avatarUrl || teacher.profileImage ||
    teacher.photo || lesson.teacherImage || DefaultTeacherImg;

  const myRating        = typeof lesson?.myRating === "number" ? lesson.myRating : 0;
  const lessonRatingAvg = typeof lesson?.ratingAvg === "number" ? lesson.ratingAvg : undefined;
  const teacherRatingAvg = typeof teacher?.ratingAvg === "number" ? teacher.ratingAvg
    : typeof teacher?.rating === "number" ? teacher.rating : undefined;

  const {
    title,
    date,
    startTime,
    endTime,
    status,
    link = "",
    price,
    type = "group",
    purchased = false, 
  } = lesson;

  const effectiveRecordings = Array.isArray(lesson?.recordings) ? lesson.recordings : [];

  const hasAnyRecording =
    effectiveRecordings.length > 0 ||
    !!lesson?.recordingUrl ||
    !!lesson?.hasRecordings;

  // A supplier only ever sees lessons from courses they created —
  // enforced server-side (see LessonsController::listMyLessons/
  // listCourseLessons, scoped by created_by_user_id) — so treating
  // every lesson a supplier can see as "purchased" is safe: it's
  // exactly the "no payment needed" behavior asked for, without a
  // second per-lesson ownership check here. Rating stays excluded
  // separately below regardless of this flag.
  const isPurchased = !!purchased || isSupplier;

  const showOfflineBadge =
    effectiveRole === "student" &&
    hasAnyRecording &&
    !isPurchased;

  const sessionPrice = typeof price === "number" && !Number.isNaN(price) ? Number(price) : null;
  const typeText     = type === "private" ? "פרטני" : "קבוצתי";
  const dateText     = date
    ? new Date(date).toLocaleDateString("he-IL", { month: "short", day: "numeric", year: "numeric" })
    : "—";

  const canJoinEff  = typeof canJoin  === "boolean" ? canJoin  : canJoinCtx(lesson, effectiveRole);
  const isInCartEff = typeof isInCart === "boolean" ? isInCart : isInCartCtx(lesson.id);
  const addToCartFn = onAddToCart ?? (() => addToCartCtx(lesson.id));

  const [myRatingValue, setMyRatingValue] = useState(myRating || 0);
  useEffect(() => { setMyRatingValue(myRating || 0); }, [myRating]);

  const handleRateChange = async (newValue) => {
    if (!canRateNow && myRatingValue <= 0) {
      showAlert?.("ניתן לדרג רק לאחר שהשיעור הסתיים.", "warning");
      return;
    }
    setMyRatingValue(newValue);
    try {
      if (onRateLesson) await onRateLesson(lesson, newValue);
      else if (typeof rateLessonCtx === "function") await rateLessonCtx(lesson.id, newValue);
    } catch (err) {
      showAlert?.(err?.message || "שגיאה בעת שליחת הדירוג.", "error");
    }
  };

  const [serverOffsetMs, setServerOffsetMs] = useState(0);
  const [, setTick] = useState(0);

  const [menuOpen, setMenuOpen] = useState(false);
  const btnRef = useRef(null);

  const lessonStartDT = useMemo(() => toLocalDateTime(date, startTime), [date, startTime]);
  const lessonEndDT   = useMemo(() => toLocalDateTime(date, endTime),   [date, endTime]);
  const serverNowMs = Date.now() + serverOffsetMs;

  const isLiveNow = lessonStartDT && lessonEndDT
    ? serverNowMs >= lessonStartDT.getTime() && serverNowMs <= lessonEndDT.getTime()
    : false;

  const isLessonFinishedByTime = lessonEndDT
    ? serverNowMs >= lessonEndDT.getTime()
    : false;

  const isLessonCompleted =
    String(status || "").toUpperCase() === "COMPLETED" || isLessonFinishedByTime;

  // Explicitly excludes isSupplier — isPurchased is now true for a
  // supplier too (see above), so without this exclusion a supplier
  // would become rating-eligible the moment isPurchased flips true.
  // Rating stays student-only regardless of purchase status.
  const canRateNow =
    effectiveRole === "student" &&
    !isSupplier &&
    isPurchased &&
    !!lesson?.canRate;

  const showStudentRatingUI =
    effectiveRole === "student" &&
    !isSupplier &&
    isPurchased &&
    (myRatingValue > 0 || !!lesson?.canRate);

  const statusUpper   = String(status || "").toUpperCase().trim();
  const isCanceled    = ["CANCELED", "CANCELLED", "NOT_HELD", "NO_SHOW"].includes(statusUpper);

  const isUpcoming = lessonStartDT ? lessonStartDT.getTime() >= serverNowMs : false;

  const showFullTeacherMenu = isTeacher && !isCanceled && !isLessonCompleted && (canJoinEff || isUpcoming);

  const actions = useMemo(() => {
    // A learning center (supplier) viewing a lesson of a course it
    // created can see who's participating, nothing else (no edit /
    // cancel — those stay with the teaching teacher).
    if (!isTeacher) {
      return isSupplier ? [{ key: "report", label: 'דו"ח משתתפים', Icon: IconReport }] : [];
    }

    const baseActions = [
      { key: "report", label: 'דו"ח משתתפים', Icon: IconReport },
    ];

    if (!showFullTeacherMenu) return baseActions;

    return [
      ...(canEdit ? [{ key: "edit", label: "עריכת שיעור", Icon: IconEdit }] : []),
      ...baseActions,
      { key: "cancel", label: "בטל שיעור", Icon: IconCancel, className: "danger" },
    ];
  }, [isTeacher, isSupplier, showFullTeacherMenu, canEdit]);

  const handleAction = (key) => {
    if (key === "edit")   onRequestEdit?.(lesson);
    if (key === "report") onShowParticipants?.(lesson);
    if (key === "cancel") onCancelSession?.(lesson);
    setMenuOpen(false);
  };

  const [meetingInfo, setMeetingInfo] = useState({
    active: false,
    data: null,
    checked: false,
    hasAnySegment: false,
    joinedCurrentSegment: false,
    isContinuation: false,
  });

  const refreshMeetingState = useCallback(async () => {
    if (!lesson?.id) return;

    if (!isLiveNow) {
      setMeetingInfo({
        active: false,
        data: null,
        checked: true,
        hasAnySegment: false,
        joinedCurrentSegment: false,
        isContinuation: false,
      });
      return;
    }

    try {
      const data = await getAvailableMeetings(lesson.id);
      const segmentCount = Number(data?.segmentCount || 0);

      const activeMeeting = !!(
        data &&
        (
          Number(data.code) === 1 ||
          data.active === true ||
          data.isActive === true ||
          data.meetingActive === true ||
          data.lessonInProgress === true ||
          data.currentMeetingActive === true
        )
      );

      const hasAnySegment = !!(
        data?.hasAnySegment ||
        segmentCount > 0 ||
        data?.lastSegmentId ||
        data?.lastMeetingId ||
        data?.currentSegmentId ||
        data?.currentMeetingId
      );

      const joinedCurrentSegment = !!(
        data?.joinedCurrentSegment ||
        data?.studentJoined ||
        data?.alreadyJoined
      );

      const isContinuation = !!(
        data?.isContinuation ||
        Number(data?.segmentIndex || 1) > 1 ||
        segmentCount > 1
      );

      setMeetingInfo({
        active: activeMeeting,
        data: data || null,
        checked: true,
        hasAnySegment,
        joinedCurrentSegment,
        isContinuation,
      });
    } catch {
      setMeetingInfo({
        active: false,
        data: null,
        checked: true,
        hasAnySegment: false,
        joinedCurrentSegment: false,
        isContinuation: false,
      });
    }
  }, [lesson?.id, isLiveNow, getAvailableMeetings]);

  useEffect(() => { refreshMeetingState(); }, [refreshMeetingState]);

  useEffect(() => {
    if (!lesson?.id || !isLiveNow) return;

    refreshMeetingState();

    const timer = setInterval(() => {
      refreshMeetingState();
    }, 5000);

    return () => clearInterval(timer);
  }, [lesson?.id, isLiveNow, refreshMeetingState]);

  useEffect(() => {
    let cancelled = false;

    const syncServerClock = async () => {
      try {
        const serverNowMs = await getServerTime();
        if (!cancelled) {
          setServerOffsetMs(serverNowMs - Date.now());
        }
      } catch (err) {
        console.warn("[LessonCard] failed to sync server clock:", err);
      }
    };

    syncServerClock();

    const syncTimer = window.setInterval(syncServerClock, 60000);
    return () => {
      cancelled = true;
      window.clearInterval(syncTimer);
    };
  }, [getServerTime]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      setTick((v) => v + 1);
    }, 1000);

    return () => window.clearInterval(timer);
  }, []);

  const teacherLessonInProgress =
    isTeacher &&
    isLiveNow &&
    meetingInfo.active;

  const teacherShouldContinue =
    isTeacher &&
    isLiveNow &&
    !meetingInfo.active &&
    meetingInfo.hasAnySegment;

  const teacherCanStartFirstSegment =
    isTeacher &&
    isLiveNow &&
    !meetingInfo.active &&
    !meetingInfo.hasAnySegment;

  const studentCanJoinFirstSegment =
    !isTeacher &&
    isLiveNow &&
    isPurchased &&
    meetingInfo.active &&
    !meetingInfo.joinedCurrentSegment &&
    !meetingInfo.isContinuation;

  const studentCanJoinNextSegment =
    !isTeacher &&
    isLiveNow &&
    isPurchased &&
    meetingInfo.active &&
    !meetingInfo.joinedCurrentSegment &&
    meetingInfo.isContinuation;

  const studentLessonInProgress =
    !isTeacher &&
    isLiveNow &&
    isPurchased &&
    meetingInfo.active &&
    meetingInfo.joinedCurrentSegment;

  const canPlayRecording =
    isTeacher ? hasAnyRecording : (hasAnyRecording && !!isPurchased);

  const handleStartLesson = useCallback(async (curLesson) => {
    if (!curLesson?.id) return;
    setStartingLessonId(curLesson.id);

    try {
      if (effectiveRole === "teacher") {
        if (meetingInfo.active && meetingInfo.data?.hostUrl) {
          window.open(meetingInfo.data.hostUrl, "_blank", "noopener,noreferrer");
          return;
        }

        const topic =
          curLesson.title || curLesson.courseTitle || `שיעור #${curLesson.id}`;

        const created = await newZoomMeeting(curLesson.id, {
          topic,
          platform: curLesson.platform || "ZOOM",
        });

        if (!created?.hostUrl) {
          showAlert?.("לא התקבלה כתובת התחברות למורה.", "error");
          return;
        }

        setMeetingInfo((prev) => ({
          ...prev,
          active: true,
          data: created || prev.data || null,
          checked: true,
          hasAnySegment: true,
          joinedCurrentSegment: false,
          isContinuation: !!prev.hasAnySegment,
        }));

        window.open(created.hostUrl, "_blank", "noopener,noreferrer");
        return;
      }

      if (!isPurchased) {
        showAlert?.("נדרש תשלום לפני ההצטרפות לשיעור.", "warning");
        return;
      }

      if (!meetingInfo.active) {
        showAlert?.("המורה עדיין לא התחיל את השיעור.", "warning");
        return;
      }

      const joined = await markStudentJoinedLesson(curLesson.id);

      setMeetingInfo((prev) => ({
        ...prev,
        joinedCurrentSegment: true,
      }));

      const finalJoinUrl = joined?.joinUrl || meetingInfo.data?.joinUrl || "";

      if (!finalJoinUrl) {
        showAlert?.("לא התקבלה כתובת הצטרפות לתלמיד.", "error");
        return;
      }

      window.open(finalJoinUrl, "_blank", "noopener,noreferrer");
    } catch (err) {
      showAlert?.(err?.message || "שגיאה בעת ניסיון להתחבר לשיעור.", "error");
    } finally {
      setStartingLessonId(null);
    }
  }, [
    effectiveRole,
    meetingInfo,
    newZoomMeeting,
    markStudentJoinedLesson,
    updateLessonAttendance,
    isPurchased,
    showAlert,
    user?.id,
  ]);

  return (
    <article className="flex w-full flex-col overflow-hidden rounded-2xl bg-white ring-1 ring-gray-100 shadow-md">
      <div className="relative h-44 flex-shrink-0 overflow-hidden sm:h-52">
        <img
          src={courseImage}
          alt={title || "Lesson"}
          className="h-full w-full object-cover"
          onError={(e) => { if (e.currentTarget.src !== DefaultCourseImg) e.currentTarget.src = DefaultCourseImg; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />

        <div className="absolute left-2.5 top-2.5 z-10 flex max-w-[calc(100%-48px)] flex-wrap gap-1.5">
          {subject && (
            <span className="rounded-lg bg-gray-700/90 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              {subject}
            </span>
          )}
          <span className="rounded-lg bg-emerald-700/50 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
            {typeText}
          </span>
          
        </div>

        {(isTeacher || isSupplier) && actions.length > 0 && (
          <div className="absolute right-2.5 top-2.5 z-20">
            <button
              ref={btnRef}
              type="button"
              onClick={(e) => { e.stopPropagation(); setMenuOpen((p) => !p); }}
              className="flex h-8 w-8 items-center justify-center rounded-full bg-white/90 text-gray-600 shadow-md transition hover:scale-105 hover:bg-white"
            >
              <svg width="18" height="18" viewBox="0 0 24 24">
                <circle cx="12" cy="5"  r="1.5" fill="currentColor" />
                <circle cx="12" cy="12" r="1.5" fill="currentColor" />
                <circle cx="12" cy="19" r="1.5" fill="currentColor" />
              </svg>
            </button>
            <KebabMenu
              open={menuOpen}
              onClose={() => setMenuOpen(false)}
              actions={actions}
              onAction={handleAction}
              dir="rtl"
              usePortal
              triggerRef={btnRef}
              position="bottom-right"
            />
          </div>
        )}

        <div className="absolute inset-x-3.5 bottom-3 z-10 flex items-center justify-between gap-2">
          
          <h3 className="line-clamp-2 text-sm font-bold leading-snug text-white drop-shadow-md sm:text-base">
            {courseName}
          </h3>

          {showOfflineBadge && (
            <span className="rounded-lg bg-red-700/50 px-2.5 py-1 text-xs font-semibold text-white backdrop-blur-sm">
              זמין אופליין
            </span>
          )}

        </div>

      </div>

      <div className="flex flex-1 flex-col p-4">
        <div className="mb-2.5 flex items-center justify-between gap-2">
          <div className="flex min-w-0 items-center gap-2">
            <img
              src={teacherImage} 
              alt={instructor}
              className="h-8 w-8 flex-shrink-0 rounded-full border-2 border-gray-100 object-cover sm:h-9 sm:w-9"
              onError={(e) => { if (e.currentTarget.src !== DefaultTeacherImg) e.currentTarget.src = DefaultTeacherImg; }}
            />
            <span className="truncate text-sm font-semibold text-emerald-600">{instructor}</span>
          </div>

          <div className="flex flex-shrink-0 items-center">
            {isTeacher ? (
              lessonRatingAvg !== undefined && lessonRatingAvg > 0 ? (
                <div className="flex items-center gap-1 text-sm font-semibold text-gray-800">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFC107">
                    <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                  </svg>
                  <span>{lessonRatingAvg.toFixed(1)}</span>
                </div>
              ) : null
            ) : !isPurchased && teacherRatingAvg !== undefined && teacherRatingAvg > 0 ? (
              <div className="flex items-center gap-1 text-sm font-semibold text-gray-800">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="#FFC107">
                  <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
                </svg>
                <span>{teacherRatingAvg.toFixed(1)}</span>
              </div>
            ) : showStudentRatingUI ? (
              <SatisfactionRatingSystem
                canRate={canRateNow}
                currentRating={myRatingValue}
                onRate={handleRateChange}
                lesson={lesson}
              />
            ) : null}
          </div>
        </div>

        {title && (
          <p className="mb-2.5 line-clamp-2 text-xs font-medium leading-relaxed text-slate-500 sm:text-sm">
            {title}
          </p>
        )}

        <div className="mb-3 flex flex-wrap items-center gap-3 text-xs text-gray-500 sm:text-sm">
          <div className="flex items-center gap-1.5">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="3" y1="10" x2="21" y2="10" />
              <circle cx="8" cy="15" r="1" fill="currentColor" />
            </svg>
            <span>{dateText}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            <span>{startTime}–{endTime}</span>
          </div>
        </div>

        <div className="mt-auto flex items-center justify-between gap-2 border-t border-gray-100 pt-3" dir="rtl">
          {sessionPrice != null ? (
            <div className="flex items-baseline gap-0.5">
              <span className="text-xl font-bold text-emerald-500 sm:text-2xl">{Number(sessionPrice).toFixed(0)}</span>
              <span className="text-base font-bold text-emerald-500">₪</span>
            </div>
          ) : (
            <div />
          )}

          <div className="flex-shrink-0">
            <SartLesson
              role={effectiveRole}
              date={date}
              startTime={startTime}
              endTime={endTime}
              status={status}
              serverNowMs={serverNowMs}
              hasRecording={hasAnyRecording}
              recordingUrl={lesson.recordingUrl || ""}
              recordings={effectiveRecordings}
              canPlayRecording={canPlayRecording}
              isInCart={isInCartEff}
              isPurchased={isPurchased}
              liveMeetingActive={meetingInfo.active}
              teacherShouldContinue={teacherShouldContinue}
              teacherLessonInProgress={teacherLessonInProgress}
              studentCanJoinFirstSegment={studentCanJoinFirstSegment}
              studentCanJoinNextSegment={studentCanJoinNextSegment}
              studentLessonInProgress={studentLessonInProgress}
              onAddToCart={addToCartFn}
              onStart={() => handleStartLesson(lesson)}
              onShowRecording={() => onShowRecording?.(lesson)}
              starting={startingLessonId === lesson.id}
            />
          </div>
        </div>
      </div>
    </article>
  );
}